/** @jsxImportSource @emotion/react */
import { ProductEsService } from '@vitafy/storefront-api-contracts-fetch';
import Cookies from 'js-cookie';
import { useContext, useEffect, useState } from 'react';

import { Image } from '@stories/atoms/Image';
import { SectionShort } from '@stories/atoms/Section/Section';
import Carousel from '@stories/organisms/Carousel';
import { PageContext } from '@stories/templates/Context/pageContext';
import { media } from '@stories/theming/settings';

import useTranslate from '../../../common/hooks/useTranslate';
import { buildMediaUrl, buildSrcSet, makeUrlAbsolute } from '../../../common/utlis';

import type { Theme } from '@emotion/react';
import type { Interpolation } from '@emotion/serialize';

const translations = {
  'Last seen products': {
    de_DE: 'Zuletzt angesehene Artikeln'
  }
};

type ProductType = {
  cssProps?: Interpolation<Theme>;
  imagePath: string;
  name: string;
  url: string;
};

const ProductTile: React.FC<ProductType> = ({ cssProps, imagePath, name, url }) => {
  return (
    <a href={makeUrlAbsolute(url)} css={{ display: 'block', ...(cssProps as object) }} title={name}>
      <Image
        src={buildMediaUrl(imagePath, { width: 300 })}
        srcset={buildSrcSet(imagePath, [150, 300, 450, 600, 800])}
        sizes="(min-width: 560px) 160rem, 30vw"
        alt={name}
      />
    </a>
  );
};

const LastSeenProducts: React.FC = () => {
  const [products, setProducts] = useState<ProductType[]>([]);
  const { storeId } = useContext(PageContext);
  const t = useTranslate(translations);

  useEffect(() => {
    const getData = async () => {
      const lspCookieValue = Cookies.get(`lsp_${storeId}`) || '[]';
      let lastSeenProductsIds = [];

      lastSeenProductsIds = JSON.parse(lspCookieValue);
      const productsData = await ProductEsService.productEsControllerGetProductsByIds({
        storeCodeOrId: storeId,
        productIds: lastSeenProductsIds
      });
      setProducts(
        productsData.map((product) => ({
          imagePath: product.image.path,
          name: product.name,
          url: product.url
        }))
      );
    };

    try {
      getData();
    } catch (error) {
      console.error(error);
    }
  }, [storeId]);

  return products.length <= 0 ? null : (
    <SectionShort headline={t('Last seen products')}>
      <Carousel seamless={true} center={true} slideGapCompensation={8}>
        {products.map((product) => (
          <ProductTile
            {...product}
            key={product.name}
            cssProps={{
              padding: '0 .5rem',
              width: '40%',
              [media('tablet')]: { width: 'calc(100% / 4)' },
              [media('desktop')]: { width: 'calc(100% / 7)' }
            }}
          />
        ))}
      </Carousel>
    </SectionShort>
  );
};

export default LastSeenProducts;
